<!-- eslint-disable vue/no-mutating-props -->
<template>
  <v-card
    id="tours_card"
    elevation="0"
    tile
  >
    <v-card-title class="tab-title d-flex">
      <v-btn
        v-if="showProviderProfileNavBtn"
        @click="emit('toggleProviderProfileNav')"
        :aria-label="$bct('Toggle provider profile navigation')"
        data-cy="provider_profile_nav_icon"
        icon="menu_open"
        variant="text"
      />

      <h2 class="v-card-title py-0 px-0 d-flex align-center">
        {{ $bct('Tour requests') }}
      </h2>
    </v-card-title>

    <v-divider />

    <v-card-text class="py-4">
      <LabeledSwitch
        v-model="provider.tours_enabled"
        :subtitle="TOUR_CONFIRM_OR_DENY_TEXT"
        data-cy="tours_enabled_switch"
        title="Allow families to request an on-site tour at your location?"
      />

      <template v-if="provider.tours_enabled">
        <LabeledSwitch
          v-model="provider.tour_groups"
          data-cy="allow_multiple_families_toggle"
          title="Allow more than one family to tour at the same time"
        />

        <v-divider class="mt-3 mb-6" />

        <v-card-text>
          <p class="c-black fs-18 fw-500">
            {{ $bct('When would you like to offer tours?') }}
          </p>

          <div
            v-for="(day, index) in hours"
            :key="index"
          >
            <v-row dense>
              <v-col
                class="d-flex align-center mb-2"
                cols="12"
              >
                <div class="fw-600 fs-18">
                  {{ $bct($a.assets.weekdays[day.day]) }}
                </div>

                <v-spacer />

                <v-switch
                  @update:model-value="(value) => updateHours(value, index)"
                  :label="hours[index].included ? $bct('Yes') : $bct('No')"
                  :model-value="hours[index].included"
                  class="ma-0"
                  color="primary"
                  data-cy="switch_on"
                  hide-details
                  inset
                />
              </v-col>

              <LabeledOpenClose
                v-show="day.included"
                @change="updateDay(index, $event)"
                :max-mins="1200"
                :min-mins="420"
                :model-value="[day.open, day.close]"
                cols="12"
              />
            </v-row>

            <v-divider
              v-if="index < hours.length - 1"
              class="my-4"
            />
          </div>

          <v-divider class="my-8" />

          <p class="c-black fs-18 fw-500 mb-2">
            {{ $bct('Tour length') }}
          </p>

          <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledSelect -->
          <v-select
            v-model="provider.tour_time"
            :item-title="(tourLength) => $bct(tourLength.text)"
            :items="[
              { text: $bct('30 minutes'), value: 30 },
              { text: $bct('1 hour'), value: 60 },
              { text: $bct('2 hours'), value: 120 },
            ]"
            data-cy="tour_length"
            variant="filled"
          />
        </v-card-text>
      </template>
    </v-card-text>
  </v-card>
</template>

<script setup>
import LabeledOpenClose from '@/shared/components/form/LabeledOpenClose.vue';
import LabeledSwitch from '@/shared/components/form/LabeledSwitch.vue';

const TOUR_CONFIRM_OR_DENY_TEXT =
  'Before a family can confirm a tour, you will be required to confirm or deny ' +
  'the request. You will receive an email whenever a family requests a tour.';

const $bct = inject('translator');

const provider = defineModel({ type: Object });

defineProps({
  showProviderProfileNavBtn: {
    type: Boolean,
    default: null,
  },
});

const emit = defineEmits(['progress', 'toggleProviderProfileNav']);

const hours = ref([
  { day: 0, open: 1700, close: 1900, included: false },
  { day: 1, open: 1700, close: 1900, included: false },
  { day: 2, open: 1700, close: 1900, included: false },
  { day: 3, open: 1700, close: 1900, included: false },
  { day: 4, open: 1700, close: 1900, included: false },
  { day: 5, open: 1700, close: 1900, included: false },
  { day: 6, open: 1700, close: 1900, included: false },
]);

watch(
  () => [
    provider.value?.tours_enabled,
    provider.value?.tour_hours?.length ?? 0,
    provider.value?.tour_time,
  ],
  calculateProgress,
  { immediate: true },
);

function calculateProgress() {
  const hasEnabled = !!provider.value?.tours_enabled;
  const hasHours = (provider.value?.tour_hours?.length || 0) > 0;
  const hasTourTime = !!provider.value?.tour_time;

  const progress = hasEnabled && hasHours && hasTourTime ? 100 : 0;
  emit('progress', progress);
}

function load() {
  calculateProgress();
  setInitialHours();
}

function syncHours() {
  provider.value.tour_hours = hours.value
    .filter((day) => day.included)
    .map((day) => ({
      day: day.day,
      open: day.open,
      close: day.close,
    }));
}

function setInitialHours() {
  if (provider.value.tour_hours) {
    hours.value = hours.value.map((newDay) => {
      const oldDayFound = provider.value.tour_hours.find((oldDay) => oldDay.day === newDay.day);
      const updatedNewDay = newDay;

      if (oldDayFound) {
        updatedNewDay.close = oldDayFound.close;
        updatedNewDay.included = true;
        updatedNewDay.open = oldDayFound.open;
        return updatedNewDay;
      }
      updatedNewDay.included = false;
      return updatedNewDay;
    });
  }
}

function updateDay(index, timeRange) {
  const [openTime, closeTime] = timeRange;
  hours.value[index].open = openTime;
  hours.value[index].close = closeTime;

  syncHours();
}

function updateHours(value, index) {
  hours.value[index].included = value;
  syncHours();
}

onMounted(load);
</script>
